import { MS } from "@p0security/util/time";

import type { AnyFilterSchema } from "../integrations/types.ts";
import type {
  Access,
  AccessRequestOf,
  ArgsOf,
  Delegate,
  Output,
  Parameter,
  PermissionOf,
  RequestOf,
  SpecToType,
  StagedRequestOf,
} from "./access/index.ts";
import type { Evidence } from "./evidence.ts";
import type { Lister, SelectItem } from "./lister.ts";
import type { AgenticContext } from "./permission.ts";

export type EmptyAccess = Access<
  Record<string, Output>,
  Record<string, Parameter>,
  Record<string, Delegate<any, any>>
>;

export type EmptyAccesses = Record<string, EmptyAccess>;

export type EmptyAccessDelegation = Record<string, Delegate<any, any>>;

export const DEFAULT_LIFECYCLE_MAX_RETRIES = 2;

export const DEFAULT_LIFECYCLE_RETRY_INTERVAL = 500 * MS;

/** Sentinel for generated properties that are unused in the access lifecycle */
export const UNUSED_STAGED_ITEM = "_unused";

export type RequestData = {
  agentic?: AgenticContext;
  principal: string;
  requestId: string;
};

export type NewRequestData = Omit<RequestData, "requestId">;

/** Identifies the Grant a lifecycle runs for, at stage, grant and revoke
 *
 * Runtime-only: the framework adds it to the request it passes a lifecycle, and it is never
 * persisted. See `toGrantId` for its format.
 */
export type GrantIdentity = { readonly grantId: string };

/**
 * A function that normalizes input arguments into a request object
 */
export type NormalizeRequestFunction<Access extends EmptyAccess> = (
  args: ArgsOf<Access>,
  request: NewRequestData
) => Promise<PermissionOf<Access>[]>;

/**
 * A function that takes an un-staged request and returns a staged request with generated data
 */
export type StageRequestFunction<Access extends EmptyAccess> = (
  request: RequestOf<Access> & GrantIdentity
) => Promise<SpecToType<Access["generated"]>>;

/**
 * A function that provisions an atomic infrastructure change for a given access step.
 */
type UpFunction<
  Access extends EmptyAccess,
  Current extends object,
  Next extends Current,
> = (
  request: StagedRequestOf<Access> & GrantIdentity,
  locals: Current,
  identity: string
) => Promise<Next>;

/**
 * A function that de-provisions an atomic infrastructure change for a given access step.
 */
type DownFunction<Access extends EmptyAccess> = (
  request: StagedRequestOf<Access> & GrantIdentity,
  identity: string
) => Promise<void>;

/** Specifies the error action to take when an error occurs during a lifecycle step
 *
 * - `abort`: Abort the lifecycle step and propagate the error
 * - `retry`: Retry the lifecycle step (default behavior)
 * - `ignore`: Ignore the error and continue
 */
export type ErrorAction = "abort" | "ignore" | "retry";

/**
 * A function that recovers from errors.
 *
 * @returns next state if the error is acceptable or {@link ErrorAction} otherwise
 */
export type UpErrorBoundaryFunction<Current extends object, Next = Current> = (
  error: unknown,
  current: Current
) => Promise<Exclude<ErrorAction, "ignore"> | Next>;

/**
 * A function that recovers from errors.
 *
 * @returns `ignore` if the error is acceptable or an {@link ErrorAction} otherwise
 */
export type DownErrorBoundaryFunction = (
  error: unknown
) => Promise<ErrorAction>;

/**
 * A function that defines a series of steps to be taken to grant and revoke access.
 */
export type StepsFunction<Access extends EmptyAccess> = (
  start: StepChain<Access, object, object>
) => StepChain<Access, any, any>;

type LifecycleFunction<Access extends EmptyAccess, Return> = (
  request: RequestOf<Access>
) => Promise<Return>;

type UpHandler<
  Access extends EmptyAccess,
  Current extends object,
  Next extends Current,
> = {
  run: UpFunction<Access, Current, Next>;
  boundary?: UpErrorBoundaryFunction<Current, Next>;
};

type DownHandler<Access extends EmptyAccess> = {
  run: DownFunction<Access>;
  boundary?: DownErrorBoundaryFunction;
};

export type StepOptions = {
  /** Controls idempotency validation (double execution of the step) in dev and stage
   * environments.
   *
   * - 'checked' (default): the step is executed twice to validate idempotency.
   * - 'unchecked': the step has not (yet) been made idempotent — a TODO, not a design
   *   decision; new additions of 'unchecked' will trigger an error.
   * - 'exempt': the step is deliberately excluded from idempotency validation (e.g. lock
   *   bracketing steps) — an intentional design decision, not a TODO.
   */
  idempotency?: "checked" | "exempt" | "unchecked";

  /** Maximum number of times this step can be retried before it is treated as
   * erroring
   */
  maxRetries?: number;
  /** Number of ms to wait between retries */
  retryInterval?: number;
};

/**
 * An atomic step that provisions and de-provisions infrastructure changes in order to grant and revoke access to a resource.
 */
export type Step<
  Access extends EmptyAccess,
  Current extends object = object,
  Next extends Current = Current,
> = {
  /** Label for this step, for use in logging */
  label: string;
  /** Provisions infrastructure changes for this access step */
  up?: UpFunction<Access, Current, Next> | UpHandler<Access, Current, Next>;
  /** Removes infrastructure changed for this access step */
  down?: DownFunction<Access> | DownHandler<Access>;
  /** Tests "acceptable" errors; use to ensure step idempotency
   */
} & StepOptions;

/** A step that does not change the context. */
export type ProcedureStep<Access extends EmptyAccess> = <
  L extends object,
>() => Step<Access, L, L>;

export type StepChain<
  Access extends EmptyAccess,
  Current extends object = object,
  Next extends Current = Current,
> = {
  /**
   * Executes the next step in the step chain.
   * @param next The next step to execute
   * @example
   * ```typescript
   * const steps = (start) => start.then({
   *      label: "Step 1",
   *      up: async (request, locals) => {
   *        // Provision infrastructure changes
   *      },
   *      down: async (request) => {
   *        // De-provision infrastructure changes
   *      }
   *    }).then({
   *      label: "Step 2",
   *      // ...
   *    })
   * ```
   */
  then: <O extends Next>(
    next: Step<Access, Next, O>
  ) => StepChain<Access, Current, O>;

  /**
   * Delegates to another lifecycle provider to execute a different step chain.
   *
   * @example
   * ```typescript
   * const steps = (start) => start.then({
   *   label: "Step 1",
   *   // ...
   * }).delegate(
   *   async () => ({
   *     provider: otherProvider,
   *     access: "otherAccess",
   *     request: (r) => ({ ...r, newField: "newField" })
   * }).then({
   *   label: "Step 2",
   *   // ...
   * })
   * ```
   *
   * @deprecated Use `delegates` in access
   */
  legacyDelegate: <
    ToContext extends object,
    ToAccesses extends Record<string, EmptyAccess>,
    ToAccessKey extends keyof ToAccesses,
  >(
    transform: OptionalLegacyDelegateTransform<
      Access,
      ToContext,
      ToAccesses,
      ToAccessKey
    >
  ) => StepChain<Access, Current, Current>;
};

export enum Listing {
  /** Lister is defined outside the lifecycle
   *
   * @deprecated Use generated lister unless caching is required
   */
  External,
  /** Lister is created via the lifecycle */
  Auto,
}

type ToFilterables<S extends AnyFilterSchema> = {
  [K in keyof S]: S[K]["keys"] extends "boolean" ? boolean : string[] | string;
};

type FilterableSelectItem<F extends AnyFilterSchema> = {
  /** Filterable values. Each property should correspond to the corresponding rule's
   *  filter keys.
   */
  filterables?: ToFilterables<F>;
} & SelectItem;

export type AnyFilterableSelectItem = FilterableSelectItem<AnyFilterSchema>;

/** Defines how this parameter should be listed in calls to `ls` and in modal drop-downs */
// TODO: Add caching
export type AccessParameterListing<
  Access extends EmptyAccess,
  FilterSchema extends AnyFilterSchema,
> = {
  type: Listing.Auto;
  /** If present, configures filtering; otherwise uses item defaults. */
  filter?: {
    /** If present, the id of the filter to apply */
    id: keyof FilterSchema & string;
    /** If present, filters the SelectItem `key` using this filter key */
    primaryKey: string;
  };
  /** Returns valid values for this field */
  list: (
    args: Partial<ArgsOf<Access>>,
    requestData: Partial<Pick<RequestData, "agentic" | "principal">>
  ) => AsyncIterable<FilterableSelectItem<FilterSchema>>;
};

export type ParameterVisibility<Access extends EmptyAccess> = {
  /** Callback to determine if an interactive modal element should be displayed */
  show?: (args: Partial<ArgsOf<Access>>) => boolean;
};

export type ParameterListing<Access extends EmptyAccess> = {
  /** Listing object for dynamic lookup of access parameters */
  listing: /** Automatically generate lister */
    | AccessParameterListing<Access, AnyFilterSchema>
    | { type: Listing.External; lister: Lister };
};

export type AccessLifecycleParameters<Access extends EmptyAccess> = {
  [K in keyof Access["parameters"] &
    string as Access["parameters"][K]["type"] extends "dynamic" | "static"
    ? K
    : never]: ParameterListing<Access>;
} & {
  [K in keyof Access["parameters"]]?: ParameterVisibility<Access>;
};

export type DelegateArgs<ToDelegate extends Delegate<any, any>> = ArgsOf<
  ToDelegate["accesses"][ToDelegate["key"]]
>;

export type DelegationConfig<
  Access extends EmptyAccess,
  D extends keyof Access["delegation"] & string,
  // Distribute over `D` so that, for an access with multiple delegates, each
  // config's `args` are correlated with its own `use` key rather than collapsing
  // to the (incompatible) intersection of every delegate's args.
> = D extends any
  ? {
      /** Key of the delegate (from `Access["delegation"]`) that this config delegates to */
      use: D;
      /** Args for the delegate's own access, translated from the current request */
      args: DelegateArgs<Access["delegation"][D]>;
      /** Runtime override of the delegate's resolution descriptor (resource/accesses/key).
       * Omit when `use` is statically known — it falls back to `Access["delegation"][use]`.
       * Only needed when the delegate set isn't known at compile time (e.g. access bundles),
       * which currently bypass this mechanism entirely rather than populating `spec`. */
      spec?: Access["delegation"][D];
      /** When true, if this delegate already has the requested grant, it is silently
       * skipped rather than failing the entire parent request. Used by access bundles,
       * where each item is an independent grant rather than a required implementation detail. */
      optional?: boolean;
    }
  : never;

export type AccessLifecycle<Access extends EmptyAccess> = {
  /** Determines when and how delegation occurs, converting the requested access into the delegated access arguments.
   * If multiple delegates are used and delegators need to resolve delegate user identity, the first delegate's identifyUser determines the user identity for the parent.
   */
  delegation: (
    request: Omit<RequestOf<Access>, "delegation">
  ) => Promise<DelegationConfig<Access, keyof Access["delegation"] & string>[]>;

  /** Resolves to true if and only if the principal already has the requested grant */
  hasGrant: LifecycleFunction<Access, boolean>;

  /** Returns the user identifier created by this lifecycle (for use by parent delegators)
   * If multiple delegates exist, the first delegate's identity is used.
   * Falls back to request.principal if not defined.
   */
  identifyUser?: (
    request: StagedRequestOf<Access> & GrantIdentity
  ) => Promise<string>;

  /** Describes interactive options for parameters */
  parameters: AccessLifecycleParameters<Access>;

  /** Converts the user-presented access arguments into a normalized set of request parameters */
  normalize: NormalizeRequestFunction<Access>;

  /** Stages this access request
   *
   * Determines any P0-generated data necessary to grant and revoke this access. For example,
   * may look up resource identifiers from user arguments.
   */
  stage: StageRequestFunction<Access>;

  /** Provisions / de-provisions this access request
   *
   * Reflects the steps taken, in order, to grant and revoke access. Steps are executed in forward
   * order to grant access, and in reverse order to revoke access.
   */
  steps: StepsFunction<Access>;
};

export type LifecycleAccesses<Accesses extends Record<string, EmptyAccess>> = {
  [K in keyof Accesses]: AccessLifecycle<Accesses[K]>;
};

export type ContextFromArgs<
  Context extends object,
  Accesses extends EmptyAccesses,
> = (
  args: Partial<ArgsOf<Accesses[keyof Accesses]>>,
  data: NewRequestData
) => Context;

type BaseAccessLifecycleProvider<
  Context extends object,
  Accesses extends EmptyAccesses,
> = {
  contextFromArgs: ContextFromArgs<Context, Accesses>;
  contextFromRequest: (
    request:
      | RequestOf<Accesses[keyof Accesses]>
      | StagedRequestOf<Accesses[keyof Accesses]>
  ) => Context;
  filterMatchingEvidence?: FilterMatchingEvidence;
  isPreapproved?: IsPreapprovedRequest<Context, Accesses>;
};

export type AccessLifecycleProvider<
  Context extends object,
  Accesses extends EmptyAccesses,
> = BaseAccessLifecycleProvider<Context, Accesses> & {
  accesses: (context: Context) => LifecycleAccesses<Accesses>;
};

export type AccessLifecycleSpec<
  Context extends object,
  Accesses extends Record<string, EmptyAccess>,
  AccessKey extends keyof Accesses & string,
> = {
  resource: string;
  accesses: Accesses;
  key: AccessKey;
  provider: AccessLifecycleProvider<Context, Accesses>;
};

/** A function that determines if a request should be pre-approved by comparing the request
 * to existing evidence.
 *
 * @returns true if the request should be pre-approved, and false otherwise.
 */
type IsPreapprovedRequest<
  Context extends object,
  Accesses extends Record<string, EmptyAccess>,
> = (
  context: Context,
  request: AccessRequestOf<Accesses, keyof Accesses & string>,
  evidence: Evidence
) => boolean;

/** A function that filters evidence candidates before pre-approval matching.
 *
 * This is useful for integrations that need to perform additional validation on evidence
 * before determining if it matches a request. For example, validating policies for
 * break-glass access.
 *
 * @param evidence - The candidate evidence to filter
 * @param getRuleApprovals - A function to get rule approvals (includes policies)
 * @returns The filtered evidence that should be considered for pre-approval
 */
export type FilterMatchingEvidence = (
  evidence: Evidence[],
  getRuleApprovals: () => Promise<any>
) => Promise<Evidence[]>;

// --- DEPRECATED LEGACY DELEGATION ---

/** @deprecated Use `delegates` in access type */
type TransformDescriptor<
  /** The current access */
  Access extends EmptyAccess,
  /** The context of the delegated steps */
  ToContext extends object,
  /** The accesses of the delegated integration */
  ToAccesses extends Record<string, EmptyAccess>,
  /** The delegated access key */
  ToAccessKey extends keyof ToAccesses,
> = {
  /** The lifecycle provider */
  provider: AccessLifecycleProvider<ToContext, ToAccesses>;
  /** The delegated access key */
  access: ToAccessKey;
  /** The delegated access request */
  toRequest: (
    fromRequest: StagedRequestOf<Access>
  ) => StagedRequestOf<ToAccesses[ToAccessKey]>;
  /** Filter predicate; if absent or true, will delegate access; if false,
   *  delegation is skipped.
   */
  predicate?: (
    fromRequest: StagedRequestOf<Access>,
    direction?: "down" | "up"
  ) => Promise<boolean>;
};

/** A function to transform steps from one lifecycle context to another.
 *
 * Note that delegation can not alter the local state.
 *
 * @deprecated Use `delegates` in access type.
 */
export type LegacyDelegateTransform<
  /** The current access */
  Access extends EmptyAccess,
  /** The context of the delegated steps */
  ToContext extends object,
  /** The accesses of the delegated integration */
  ToAccesses extends Record<string, EmptyAccess>,
  /** The delegated access key */
  ToAccessKey extends keyof ToAccesses,
> = (
  request: StagedRequestOf<Access>
) => Promise<TransformDescriptor<Access, ToContext, ToAccesses, ToAccessKey>>;

/** Like {@link LegacyDelegateTransform}, but delegation can be conditionally skipped.
 *
 * To skip delegation, return a false-y transform.
 *
 * @deprecated Use `delegates` in access type.
 */
export type OptionalLegacyDelegateTransform<
  Access extends EmptyAccess,
  ToContext extends object,
  ToAccesses extends Record<string, EmptyAccess>,
  ToAccessKey extends keyof ToAccesses,
> = (
  request: StagedRequestOf<Access>
) => Promise<
  TransformDescriptor<Access, ToContext, ToAccesses, ToAccessKey> | undefined
>;
